import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import socketIOClient from "socket.io-client";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker";
import { Helmet } from "react-helmet-async";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import FormatTime from "../../AdsPage/FormatTime";
import {
  cardHover,
  cardOutline,
  fonts,
  headingFonts,
  textFieldStyles,
} from "../../../Styles/Styles";
import ChatNotFound from "./ChatNotFound";

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [chatNotFound, setChatNotFound] = useState(false);
  const [userId, setUserId] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [adDetails, setAdDetails] = useState(null); // Store ad details
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");
  const socketRef = useRef();
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    if (!user || !token) return;
    setUserId(user._id);

    const ENDPOINT = process.env.REACT_APP_BACKEND_URL;

    socketRef.current = socketIOClient(ENDPOINT, { transports: ["websocket"] });
    socketRef.current.emit("join-chat", chatId);

    socketRef.current.on("new-message", (newMessage) => {
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    });

    socketRef.current.on("typing", (status) => {
      setIsTyping(status);
    });

    const fetchChatMessages = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/chat/messages/${chatId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 404) {
          setChatNotFound(true);
          return;
        }

        const data = await response.json();

        setMessages(data.messages || []);
        setOtherUser(data.otherUser);

        // Fetch ad details using adId
        if (data.adId) {
          const adResponse = await fetch(
            `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/${data.adId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const adData = await adResponse.json();
          setAdDetails(adData); // Set ad details
        }
      } catch (error) {
        console.error(
          "Error fetching chat messages or ad details:",
          error.message
        );
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchChatMessages();

    return () => {
      socketRef.current.disconnect();
    };
  }, [chatId]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!message.trim()) return;

    try {
      setIsSending(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/chat/messages/${chatId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ content: message.trim() }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (
          response.status === 410 &&
          errorData.error ===
            "Cannot contact this user. They may have been removed."
        ) {
          setSnackbarMessage(
            "Cannot contact this user. They may have been removed."
          );
          setSnackbarOpen(true);
          return; // Exit early, no message sent
        }
        throw new Error(errorData.error || "Failed to send message");
      }

      const newMessage = await response.json();

      setMessages((prevMessages) => [...prevMessages, newMessage]);
      socketRef.current.emit("send-message", newMessage);
    } catch (error) {
      console.error("Error sending message:", error.message);
    } finally {
      setIsSending(false); // Re-enable the button
    }

    setMessage("");
  };

  const handleTyping = () => {
    socketRef.current.emit("typing", true);
    setTimeout(() => {
      socketRef.current.emit("typing", false);
    }, 1000);
  };

  const handleViewAdClick = () => {
    navigate(`/ad/${adDetails.uniqueId}`);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  return (
    <Box
      sx={{
        mt: 5,
        mb: 5,
        px: 2,
        padding: 3,
        maxWidth: 1200,
        margin: "25px auto",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loadingMessages ? (
        <LoadingAnimation />
      ) : chatNotFound ? (
        <ChatNotFound />
      ) : (
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {/* Chat Messages Section */}
          <Grid item xs={12} md={8}>
            <Helmet>
              <title>{otherUser?.firstName || "Chat"} - Listed.ky</title>
              <meta
                name="description"
                content={`${otherUser?.firstName || "Chat"} - Listed.ky`}
              />
            </Helmet>
            <AnalyticsTracker />
            <Box>
              <Box
                sx={{
                  padding: 1.5,
                  gap: 1.5, // Adds spacing between elements
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderTopLeftRadius: "8px", // Top-left corner
                  borderTopRightRadius: "8px", // Top-right corner
                }}
              >
                {otherUser !== null ? (
                  <>
                    <Link
                      to="/inbox"
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronLeftIcon />
                    </Link>
                    <Link to={`/profile/${otherUser._id}`}>
                      <img
                        src={
                          otherUser.image ||
                          "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
                        }
                        alt={otherUser.firstName}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    </Link>
                    <Link to={`/profile/${otherUser._id}`}>
                      <Typography
                        variant="h6"
                        sx={{
                          ...headingFonts,
                          color: "var(--primary-color)",
                          whiteSpace: "nowrap", // Prevents wrapping
                          overflow: "hidden",
                          textOverflow: "ellipsis", // Adds "..." if the name is too long
                        }}
                      >
                        {otherUser.firstName}
                      </Typography>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/inbox"
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronLeftIcon />
                    </Link>
                    <img
                      src="https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
                      alt="Unknown"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        ...headingFonts,
                        color: "var(--primary-color)",
                        whiteSpace: "nowrap", // Prevents wrapping
                        overflow: "hidden",
                        textOverflow: "ellipsis", // Adds "..." if the name is too long
                      }}
                    >
                      Unknown User
                    </Typography>
                  </>
                )}
              </Box>

              {/* Chat messages container */}
              <Box
                sx={{
                  height: 500,
                  padding: 2,
                  overflowY: "auto",
                  backgroundColor: "#fff",
                  ...cardOutline,
                }}
                ref={messagesContainerRef}
              >
                <List>
                  {messages.map((msg, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        justifyContent:
                          msg.sender?._id === userId
                            ? "flex-end"
                            : "flex-start",
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: 1,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "70%",
                          padding: "5px 20px",
                          borderRadius: 1,
                          bgcolor:
                            msg.sender?._id === userId ? "#e8f5e9" : "#f1f1f1",
                          border:
                            msg.sender?._id === userId
                              ? "1px solid #4caf50"
                              : "1px solid #ddd",
                          textAlign:
                            msg.sender?._id === userId ? "right" : "left",
                          wordWrap: "break-word", // Ensures long words break properly
                          overflowWrap: "break-word", // Ensures text breaks within the box
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              sx={{
                                ...fonts,
                                whiteSpace: "pre-wrap", // Allows proper text wrapping
                                overflowWrap: "break-word", // Breaks long words to fit within the box
                              }}
                            >
                              {msg.content}
                            </Typography>
                          }
                          secondary={
                            <Tooltip
                              title={`${new Date(
                                msg.timestamp
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true, // Ensures 12-hour format
                              })} | ${new Date(
                                msg.timestamp
                              ).toLocaleDateString([], {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}`}
                            >
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{
                                  ...fonts,
                                  fontSize: "0.8rem",
                                }}
                              >
                                <FormatTime createdAt={msg.timestamp} />
                              </Typography>
                            </Tooltip>
                          }
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
                {snackbarOpen && (
                  <Box
                    sx={{
                      backgroundColor: "#f44336", // red
                      color: "white",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      fontWeight: 500,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {snackbarMessage}
                    </Typography>
                    <IconButton
                      onClick={handleSnackbarClose}
                      sx={{ color: "white", ml: 2 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {isTyping && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2, fontStyle: "italic" }}
                >
                  {otherUser?.firstName || "User"} is typing...
                </Typography>
              )}

              <Box
                sx={{
                  padding: 2,
                  gap: 2,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderBottomLeftRadius: "8px", // Top-left corner
                  borderBottomRightRadius: "8px", // Top-right corner
                }}
              >
                <TextField
                  label="Type your message"
                  fullWidth
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    handleTyping();
                  }}
                  onKeyUp={(e) => {
                    if (
                      e.key === "Enter" &&
                      !isSending &&
                      message.trim() !== ""
                    ) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  sx={{ ...textFieldStyles }}
                />
                <IconButton
                  onClick={sendMessage}
                  disabled={isSending || message.trim() === ""}
                  sx={{
                    backgroundColor: "var(--primary-color)",
                    borderRadius: "50%", // Makes it circular
                    padding: "10px 10px",
                    color: "white", // Ensures icon is visible
                    "&:focus, &:active": { outline: "none" },
                    "&:hover": {
                      backgroundColor: "var(--primary-color)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "gray", // Optional: style for disabled state
                      color: "white",
                      cursor: "not-allowed",
                    },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          {/* Ad Preview Section */}
          <Grid item xs={12} md={4}>
            {adDetails && (
              <Card
                sx={{
                  maxWidth: "auto",
                  position: "relative",
                  mb: 2,
                  ...cardOutline,
                  ...cardHover,
                }}
              >
                {adDetails.isSold && (
                  <div
                    style={{
                      top: "0",
                      position: "absolute",
                      zIndex: 1,
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      backgroundColor: "#ff0019",
                      color: "white",
                      padding: "5px 10px",
                    }}
                  >
                    Sold
                  </div>
                )}
                <CardMedia
                  sx={{
                    height: 180,
                    cursor: "pointer",
                    backgroundColor: "#F1F3F4", // Black background for uncovered areas
                    backgroundSize: "contain", // Ensure the image fits within the bounds while maintaining aspect ratio
                    backgroundRepeat: "no-repeat", // Prevent the image from repeating
                    backgroundPosition: "center", // Center the image
                    backgroundImage: `url(${adDetails.images[0].thumbnail})`, // Use the image as the background
                  }}
                  title={adDetails.title}
                  onClick={handleViewAdClick}
                />
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Chip
                      label={`$ ${adDetails.price.toLocaleString()}`}
                      sx={{
                        fontFamily: "ClashGrotesk-SemiBold",
                        letterSpacing: 1,
                        fontSize: "1rem",
                        color: "white",
                        borderRadius: "0",
                        backgroundColor: "var(--primary-color)",
                      }}
                    />
                  </Box>

                  <Typography
                    gutterBottom
                    variant="body1"
                    component="div"
                    onClick={handleViewAdClick}
                    sx={{
                      ...headingFonts,
                      cursor: "pointer",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      WebkitLineClamp: 2, // Limits the title to 2 lines
                      lineHeight: "1.2em", // Adjust line height to ensure proper spacing
                      maxHeight: "2.4em", // Maximum height for 2 lines based on line height
                    }}
                  >
                    <strong>{adDetails.title}</strong>
                  </Typography>

                  {/* Category and Location Section */}
                  <Typography
                    variant="body2"
                    sx={{
                      ...fonts,
                      fontSize: "1em",
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em",
                    }}
                  >
                    <CategoryIcon
                      sx={{
                        fontSize: "1em",
                        color: "var(--secondary-color)",
                      }}
                    />{" "}
                    {/* Category Icon */}
                    {adDetails.category}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      ...fonts,
                      fontSize: "1em",
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em",
                    }}
                  >
                    <LocationOnIcon
                      sx={{
                        fontSize: "1em",
                        color: "var(--secondary-color)",
                      }}
                    />{" "}
                    {/* Location Icon */}
                    {adDetails.location}
                  </Typography>

                  {/* Date Section */}
                  <Typography
                    variant="body2"
                    sx={{
                      ...fonts,
                      fontSize: "1em",
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em",
                    }}
                  >
                    <ScheduleIcon
                      sx={{
                        fontSize: "1em",
                        color: "var(--secondary-color)",
                      }}
                    />
                    {/* Date Icon, can also use specific Material icons */}
                    <FormatTime createdAt={adDetails.createdAt} />
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ChatPage;
